<template>
  <div class="delivery-page">
    <template v-if="!workspace.project">
      <header class="page-head">
        <div>
          <h2>设计交付</h2>
          <p>{{ projects.length }} 个界面文件夹</p>
        </div>
        <button v-if="canEdit" class="primary-button" type="button" @click="openProjectModal()"><span>+</span> 新建文件夹</button>
      </header>

      <div v-if="loading" class="page-state">正在读取...</div>
      <section v-else-if="projects.length" class="folder-grid" aria-label="界面文件夹">
        <article v-for="project in projects" :key="project.id" class="folder-item">
          <button class="folder-open" type="button" @click="selectProject(project.id)">
            <span class="folder-shape" aria-hidden="true"><span></span></span>
            <span class="folder-copy">
              <strong :title="project.projectName">{{ project.projectName }}</strong>
              <span v-if="project.themeName" class="folder-theme">{{ project.themeName }}</span>
              <small>{{ clientLabel(project.clientType) }} · {{ project.screenCount || 0 }} 个界面</small>
              <small>{{ project.assetCount || 0 }} 个图标 · {{ formatTime(project.updatedAt || project.createdAt) }}</small>
            </span>
          </button>
          <button v-if="canEdit" class="folder-edit" type="button" title="修改文件夹" aria-label="修改文件夹" @click="openProjectModal(project)">✎</button>
        </article>
      </section>
      <div v-else class="page-state page-state--empty">
        <span class="empty-folder" aria-hidden="true"></span>
        <strong>还没有界面文件夹</strong>
        <button v-if="canEdit" class="primary-button" type="button" @click="openProjectModal()">新建文件夹</button>
      </div>
    </template>

    <template v-else>
      <nav class="breadcrumb" aria-label="当前位置">
        <button type="button" @click="closeFolder">设计交付</button>
        <span>/</span>
        <strong>{{ workspace.project.projectName }}</strong>
      </nav>

      <header class="collection-head">
        <div>
          <div class="collection-title">
            <span class="small-folder" aria-hidden="true"></span>
            <h2>{{ workspace.project.projectName }}</h2>
            <button v-if="canEdit" class="bare-icon" type="button" title="修改文件夹名称" aria-label="修改文件夹名称" @click="openProjectModal(workspace.project)">✎</button>
          </div>
          <p><span v-if="workspace.project.themeName">{{ workspace.project.themeName }} · </span>{{ workspace.screens.length }} 个界面 · {{ workspace.assets.length }} 个关联图标 · {{ pendingCount }} 个待开发</p>
        </div>
        <div class="collection-actions">
          <button v-if="canAiImage" class="ai-image-button" type="button" @click="openAiGenerator">AI 生图</button>
          <label v-if="canEdit" class="upload-button" :class="{ disabled: uploading }">
            <input hidden multiple type="file" accept=".png,.jpg,.jpeg,.gif,.webp" :disabled="uploading" @change="handleScreenFiles" />
            <span>＋</span>{{ uploading ? `上传中 ${uploadProgress}%` : "上传界面图" }}
          </label>
        </div>
      </header>

      <section class="control-bar">
        <label class="search-box">
          <span aria-hidden="true">⌕</span>
          <input v-model.trim="screenKeyword" type="search" placeholder="搜索界面名称" aria-label="搜索界面名称" />
        </label>
        <div class="status-tabs" role="tablist" aria-label="界面状态">
          <button type="button" :class="{ active: statusFilter === 'ALL' }" @click="statusFilter = 'ALL'">全部 {{ workspace.screens.length }}</button>
          <button type="button" :class="{ active: statusFilter === 'PENDING' }" @click="statusFilter = 'PENDING'">待开发 {{ pendingCount }}</button>
          <button type="button" :class="{ active: statusFilter === 'COMPLETED' }" @click="statusFilter = 'COMPLETED'">已完成 {{ completedCount }}</button>
        </div>
      </section>

      <div v-if="workspaceLoading" class="page-state">正在读取...</div>
      <section v-else-if="visibleScreens.length" class="screen-grid" aria-label="界面集合">
        <button v-for="screen in visibleScreens" :key="screen.id" class="screen-file" type="button" @click="openScreen(screen)">
          <span class="screen-thumb">
            <img
              :src="resolveAssetUrl(screen.previewUrl)"
              :alt="screen.screenName"
              :class="{ 'is-wide-artwork': isBannerScreen(screen) }"
              loading="lazy"
            />
            <span class="status-mark" :class="{ done: isCompleted(screen) }">{{ isCompleted(screen) ? "已完成" : "待开发" }}</span>
          </span>
          <span class="screen-file__copy">
            <strong :title="screen.screenName">{{ screen.screenName }}</strong>
            <small>{{ screenSubtitle(screen) }} · {{ formatTime(screen.updatedAt || screen.createdAt) }}</small>
          </span>
        </button>
      </section>
      <div v-else class="page-state page-state--empty collection-empty">
        <strong>{{ workspace.screens.length ? "没有找到符合条件的界面" : "这个文件夹里还没有界面" }}</strong>
        <label v-if="canEdit && !workspace.screens.length" class="primary-button">
          <input hidden multiple type="file" accept=".png,.jpg,.jpeg,.gif,.webp" @change="handleScreenFiles" />
          上传第一张界面图
        </label>
      </div>

    </template>

    <input ref="iconInput" hidden multiple type="file" accept=".png,.jpg,.jpeg,.gif,.webp" @change="handleIconFiles" />

    <div v-if="selectedScreen" class="detail-backdrop" @click.self="selectedScreen = null">
      <section class="detail-dialog" role="dialog" aria-modal="true" aria-label="界面详情">
        <header class="detail-head">
          <div>
            <span class="status-mark" :class="{ done: isCompleted(selectedScreen) }">{{ isCompleted(selectedScreen) ? "已完成" : "待开发" }}</span>
            <h3>{{ selectedScreen.screenName }}</h3>
          </div>
          <div class="detail-actions">
            <button
              class="prompt-copy-button"
              :class="{ copied: copiedPromptScreenId === selectedScreen.id }"
              type="button"
              @click="copyScreenAssetUrls(selectedScreen)"
            >
              {{ copiedPromptScreenId === selectedScreen.id ? "已复制地址" : "复制地址" }}
            </button>
            <button
              v-if="canComplete"
              class="completion-button"
              :class="{ 'completion-button--undo': isCompleted(selectedScreen) }"
              type="button"
              :disabled="completionId === selectedScreen.id"
              @click="toggleCompleted(selectedScreen)"
            >
              {{ completionId === selectedScreen.id ? "处理中..." : isCompleted(selectedScreen) ? "恢复待开发" : "标记已完成" }}
            </button>
            <button v-if="canEdit" class="plain-button" type="button" @click="openRenameModal(selectedScreen)">重命名</button>
            <button v-if="canEdit" class="delete-button" type="button" @click="removeScreen(selectedScreen)">删除</button>
          </div>
          <button type="button" title="关闭" aria-label="关闭" @click="selectedScreen = null">×</button>
        </header>

        <div class="detail-body">
          <div class="detail-preview-pane">
            <button class="detail-preview" type="button" title="查看大图" @click="previewScreen = selectedScreen">
              <img :src="resolveAssetUrl(selectedScreen.previewUrl)" :alt="selectedScreen.screenName" />
              <span>查看大图</span>
            </button>
          </div>

          <section class="icon-section">
            <header>
              <div><h4>关联图标</h4><span>{{ selectedScreenAssets.length }} 个</span></div>
              <button v-if="canEdit" type="button" :disabled="uploading" @click="chooseIcons(selectedScreen.id)">＋ 添加图标</button>
            </header>
            <div v-if="selectedScreenAssets.length" class="icon-grid">
              <div v-for="asset in selectedScreenAssets" :key="asset.id" class="icon-item">
                <button class="icon-preview" type="button" :title="asset.originalName" @click="openAsset(asset)">
                  <img :src="resolveAssetUrl(asset.fileUrl)" :alt="asset.assetName" loading="lazy" />
                </button>
                <div class="icon-copy">
                  <strong :title="asset.originalName">{{ asset.assetName }}</strong>
                  <span>{{ formatFileSize(asset.fileSize) }}</span>
                </div>
                <a :href="resolveAssetUrl(asset.fileUrl)" :download="asset.originalName" title="下载图标" aria-label="下载图标">↓</a>
                <button v-if="canEdit" class="icon-delete" type="button" title="删除图标" aria-label="删除图标" @click="removeAsset(asset)">×</button>
              </div>
            </div>
            <button v-else-if="canEdit" class="empty-icons" type="button" @click="chooseIcons(selectedScreen.id)">＋ 为这个界面添加图标</button>
            <div v-else class="empty-icons empty-icons--readonly">暂无关联图标</div>
          </section>
        </div>
      </section>
    </div>

    <div v-if="projectModal" class="modal-backdrop" @click.self="projectModal = false">
      <form class="modal-panel" @submit.prevent="saveProject">
        <header><h3>{{ projectForm.id ? "修改文件夹" : "新建文件夹" }}</h3><button type="button" title="关闭" @click="projectModal = false">×</button></header>
        <label><span>文件夹名称</span><input v-model.trim="projectForm.projectName" required maxlength="96" autofocus placeholder="例如：小程序首页改版" /></label>
        <label><span>使用端</span><select v-model="projectForm.clientType" @change="syncProjectTheme"><option value="MINI_APP">小程序</option><option value="ADMIN_WEB">管理端</option><option value="OTHER">其他</option></select></label>
        <label><span>关联主题</span><select v-model="projectForm.themeId"><option :value="null">不关联主题</option><option v-for="theme in availableThemes" :key="theme.id" :value="theme.id">{{ theme.themeName }}{{ theme.status === 1 ? "" : "（停用）" }}</option></select></label>
        <footer><button class="plain-button" type="button" @click="projectModal = false">取消</button><button class="primary-button" type="submit" :disabled="savingProject">{{ savingProject ? "保存中..." : "保存" }}</button></footer>
      </form>
    </div>

    <div v-if="renameModal" class="modal-backdrop" @click.self="renameModal = false">
      <form class="modal-panel modal-panel--small" @submit.prevent="saveScreenName">
        <header><h3>修改界面名称</h3><button type="button" title="关闭" @click="renameModal = false">×</button></header>
        <label><span>界面名称</span><input v-model.trim="renameName" required maxlength="96" autofocus /></label>
        <footer><button class="plain-button" type="button" @click="renameModal = false">取消</button><button class="primary-button" type="submit" :disabled="savingScreen">保存</button></footer>
      </form>
    </div>

    <div v-if="previewScreen" class="lightbox" @click.self="previewScreen = null">
      <header><strong>{{ previewScreen.screenName }}</strong><button type="button" title="关闭" @click="previewScreen = null">×</button></header>
      <img :src="resolveAssetUrl(previewScreen.previewUrl)" :alt="previewScreen.screenName" />
    </div>

    <div v-if="uploading" class="upload-toast"><span :style="{ width: `${uploadProgress}%` }"></span><strong>正在上传 {{ uploadProgress }}%</strong></div>
    <div v-if="toast" class="toast" :class="{ 'toast--error': toastError }" role="status">{{ toast }}</div>
  </div>
</template>

<script>
import {
  createDesignProject,
  deleteDesignAsset,
  deleteDesignScreen,
  fetchDesignProjects,
  fetchDesignThemes,
  fetchDesignWorkspace,
  setDesignScreenCompleted,
  updateDesignProject,
  updateDesignScreen,
  uploadDesignAssets,
  uploadDesignScreens
} from "../api/officeDesign"
import { hasAdminPermission } from "../utils/adminSession"
import { resolveApiOrigin, resolveBackendAssetUrl } from "../utils/apiEndpoint"

const emptyWorkspace = () => ({ project: null, screens: [], assets: [] })

export default {
  name: "DesignDeliveryView",
  data() {
    return {
      projects: [],
      themes: [],
      selectedProjectId: null,
      workspace: emptyWorkspace(),
      loading: false,
      workspaceLoading: false,
      statusFilter: "ALL",
      screenKeyword: "",
      selectedScreen: null,
      uploading: false,
      uploadProgress: 0,
      iconTargetScreenId: null,
      completionId: null,
      copiedPromptScreenId: null,
      promptCopyTimer: null,
      projectModal: false,
      projectForm: {},
      savingProject: false,
      renameModal: false,
      renameScreenId: null,
      renameName: "",
      savingScreen: false,
      previewScreen: null,
      toast: "",
      toastError: false,
      toastTimer: null
    }
  },
  computed: {
    canEdit() { return hasAdminPermission("office:design:edit") },
    canAiImage() { return hasAdminPermission("operation:ai_image") },
    canComplete() { return hasAdminPermission("office:design:complete") },
    pendingCount() { return this.workspace.screens.filter(screen => !this.isCompleted(screen)).length },
    completedCount() { return this.workspace.screens.filter(screen => this.isCompleted(screen)).length },
    selectedScreenAssets() { return this.selectedScreen ? this.assetsForScreen(this.selectedScreen.id) : [] },
    availableThemes() { return this.themes.filter(theme => theme.clientType === this.projectForm.clientType) },
    visibleScreens() {
      const keyword = this.screenKeyword.toLowerCase()
      return this.workspace.screens.filter(screen => {
        const matchesStatus = this.statusFilter === "ALL" || (this.statusFilter === "COMPLETED" ? this.isCompleted(screen) : !this.isCompleted(screen))
        const matchesKeyword = !keyword || String(screen.screenName || "").toLowerCase().includes(keyword)
        return matchesStatus && matchesKeyword
      })
    }
  },
  mounted() { Promise.all([this.loadProjects(), this.loadThemes()]) },
  beforeDestroy() {
    clearTimeout(this.toastTimer)
    clearTimeout(this.promptCopyTimer)
  },
  methods: {
    resolveAssetUrl(value) { return resolveBackendAssetUrl(value) },
    isCompleted(screen) { return screen && screen.status === "COMPLETED" },
    assetsForScreen(screenId) { return this.workspace.assets.filter(asset => asset.screenId === screenId) },
    isBannerScreen(screen) { return screen && screen.pageCode === "home.banner" },
    screenSubtitle(screen) {
      // 轮播图卡片显示跳转目标，比「0 个图标」更有参考价值
      if (this.isBannerScreen(screen)) {
        return screen.routePath ? "跳转 " + screen.routePath : "点击不跳转"
      }
      return this.assetsForScreen(screen.id).length + " 个图标"
    },
    clientLabel(value) { return { MINI_APP: "小程序", ADMIN_WEB: "管理端", OTHER: "其他" }[value] || "未分类" },
    async loadThemes() {
      try {
        const res = await fetchDesignThemes()
        this.themes = Array.isArray(res.data) ? res.data : []
      } catch (error) {
        this.showToast(error.message || "主题读取失败", true)
      }
    },
    async loadProjects(preferredId) {
      this.loading = true
      try {
        const res = await fetchDesignProjects()
        this.projects = Array.isArray(res.data) ? res.data : []
        if (preferredId) await this.selectProject(preferredId)
        else this.closeFolder()
      } catch (error) {
        this.showToast(error.message || "文件夹读取失败", true)
      } finally {
        this.loading = false
      }
    },
    async selectProject(projectId) {
      if (!projectId) return
      this.selectedProjectId = projectId
      this.workspaceLoading = true
      this.selectedScreen = null
      try {
        const res = await fetchDesignWorkspace(projectId)
        this.workspace = res.data || emptyWorkspace()
      } catch (error) {
        this.showToast(error.message || "交付内容读取失败", true)
      } finally {
        this.workspaceLoading = false
      }
    },
    closeFolder() {
      this.selectedProjectId = null
      this.workspace = emptyWorkspace()
      this.statusFilter = "ALL"
      this.screenKeyword = ""
      this.selectedScreen = null
    },
    openScreen(screen) { this.selectedScreen = screen },
    absoluteAssetUrl(value) {
      const current = String(value || "").trim()
      if (!current || current.startsWith("data:") || current.startsWith("blob:")) return current
      if (/^https?:\/\//i.test(current)) return current
      if (current.startsWith("//")) return `${window.location.protocol}${current}`
      return `${resolveApiOrigin().replace(/\/$/, "")}/${current.replace(/^\/+/, "")}`
    },
    buildScreenAssetUrls(screen) {
      const screenUrl = this.absoluteAssetUrl(screen.sourceFileUrl || screen.previewUrl)
      const assetEntries = this.assetsForScreen(screen.id)
        .map(asset => ({
          name: String(asset.assetName || asset.originalName || "").trim(),
          url: this.absoluteAssetUrl(asset.fileUrl)
        }))
        .filter((asset, index, assets) => {
          return asset.url && asset.url !== screenUrl
            && assets.findIndex(item => item.url === asset.url) === index
        })
      const lines = screenUrl ? ["界面地址：", screenUrl] : []
      if (assetEntries.length) {
        lines.push(
          "",
          "相关资源（数据库图标名称与地址）：",
          ...assetEntries.flatMap(asset => [
            `- 图标名称（数据库）：${asset.name || "未命名图标"}`,
            `  图标地址：${asset.url}`
          ])
        )
      }
      return lines.join("\n")
    },
    async copyText(text) {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text)
        return
      }
      const input = document.createElement("textarea")
      input.value = text
      input.setAttribute("readonly", "readonly")
      input.style.position = "fixed"
      input.style.opacity = "0"
      document.body.appendChild(input)
      input.select()
      const copied = document.execCommand("copy")
      document.body.removeChild(input)
      if (!copied) throw new Error("浏览器未允许复制")
    },
    async copyScreenAssetUrls(screen) {
      if (!screen) return
      try {
        const urls = this.buildScreenAssetUrls(screen)
        if (!urls) throw new Error("暂无可复制的地址")
        await this.copyText(urls)
        this.copiedPromptScreenId = screen.id
        clearTimeout(this.promptCopyTimer)
        this.promptCopyTimer = setTimeout(() => {
          if (this.copiedPromptScreenId === screen.id) this.copiedPromptScreenId = null
        }, 1800)
        this.showToast("页面和关联资源地址已复制")
      } catch (error) {
        this.showToast(error.message || "地址复制失败", true)
      }
    },
    openAiGenerator() {
      this.$router.push({ path: "/ai-images", query: { projectId: this.selectedProjectId } })
    },
    openProjectModal(project) {
      this.projectForm = project
        ? { ...project, themeId: project.themeId || null }
        : { projectName: "", clientType: "MINI_APP", themeId: null, status: "DRAFT" }
      this.projectModal = true
    },
    syncProjectTheme() {
      if (!this.availableThemes.some(theme => theme.id === this.projectForm.themeId)) {
        this.projectForm.themeId = null
      }
    },
    async saveProject() {
      if (!this.projectForm.projectName) return
      this.savingProject = true
      try {
        const payload = {
          projectName: this.projectForm.projectName,
          clientType: this.projectForm.clientType || "MINI_APP",
          themeId: this.projectForm.themeId || null,
          status: this.projectForm.status || "DRAFT",
          versionName: this.projectForm.versionName || null,
          description: this.projectForm.description || null,
          ownerAccountId: this.projectForm.ownerAccountId || null,
          dueDate: this.projectForm.dueDate || null
        }
        const res = this.projectForm.id
          ? await updateDesignProject(this.projectForm.id, payload)
          : await createDesignProject(payload)
        this.projectModal = false
        await this.loadProjects(res.data.id)
        this.showToast("文件夹已保存")
      } catch (error) {
        this.showToast(error.message || "文件夹保存失败", true)
      } finally {
        this.savingProject = false
      }
    },
    handleScreenFiles(event) {
      const files = Array.from(event.target.files || [])
      event.target.value = ""
      this.uploadScreens(files)
    },
    async uploadScreens(files) {
      if (!files.length || !this.selectedProjectId) return
      this.uploading = true
      this.uploadProgress = 0
      try {
        await uploadDesignScreens(this.selectedProjectId, files, this.trackUpload)
        await this.refreshWorkspace()
        this.statusFilter = "ALL"
        this.showToast(`已上传 ${files.length} 张界面图，名称已使用图片文件名`)
      } catch (error) {
        this.showToast(error.message || "界面图上传失败", true)
      } finally {
        this.uploading = false
      }
    },
    chooseIcons(screenId) {
      this.iconTargetScreenId = screenId
      this.$refs.iconInput.click()
    },
    async handleIconFiles(event) {
      const files = Array.from(event.target.files || [])
      event.target.value = ""
      if (!files.length || !this.iconTargetScreenId) return
      this.uploading = true
      this.uploadProgress = 0
      try {
        await uploadDesignAssets(this.selectedProjectId, this.iconTargetScreenId, files, this.trackUpload)
        await this.refreshWorkspace()
        this.showToast(`已关联 ${files.length} 个图标`)
      } catch (error) {
        this.showToast(error.message || "图标上传失败", true)
      } finally {
        this.uploading = false
        this.iconTargetScreenId = null
      }
    },
    trackUpload(event) {
      if (event.total) this.uploadProgress = Math.round(event.loaded * 100 / event.total)
    },
    openRenameModal(screen) {
      this.renameScreenId = screen.id
      this.renameName = screen.screenName
      this.renameModal = true
    },
    async saveScreenName() {
      if (!this.renameName || !this.renameScreenId) return
      this.savingScreen = true
      try {
        await updateDesignScreen(this.renameScreenId, { screenName: this.renameName })
        this.renameModal = false
        await this.refreshWorkspace()
        this.showToast("界面名称已修改")
      } catch (error) {
        this.showToast(error.message || "名称修改失败", true)
      } finally {
        this.savingScreen = false
      }
    },
    async toggleCompleted(screen) {
      this.completionId = screen.id
      try {
        await setDesignScreenCompleted(screen.id, !this.isCompleted(screen))
        await this.refreshWorkspace()
        const current = this.workspace.screens.find(item => item.id === screen.id)
        this.showToast(this.isCompleted(current) ? "已标记完成" : "已恢复为待开发")
      } catch (error) {
        this.showToast(error.message || "状态更新失败", true)
      } finally {
        this.completionId = null
      }
    },
    async removeScreen(screen) {
      if (!window.confirm(`删除“${screen.screenName}”及其关联图标吗？`)) return
      try {
        await deleteDesignScreen(screen.id)
        this.selectedScreen = null
        await this.refreshWorkspace()
        this.showToast("界面已删除")
      } catch (error) {
        this.showToast(error.message || "删除失败", true)
      }
    },
    async removeAsset(asset) {
      if (!window.confirm(`删除图标“${asset.originalName}”吗？`)) return
      try {
        await deleteDesignAsset(asset.id)
        await this.refreshWorkspace()
        this.showToast("图标已删除，界面已恢复为待开发")
      } catch (error) {
        this.showToast(error.message || "图标删除失败", true)
      }
    },
    openAsset(asset) { window.open(this.resolveAssetUrl(asset.fileUrl), "_blank", "noopener") },
    async refreshWorkspace() {
      const openScreenId = this.selectedScreen && this.selectedScreen.id
      await Promise.all([this.selectProjectData(this.selectedProjectId), this.refreshProjectList()])
      this.selectedScreen = openScreenId ? this.workspace.screens.find(screen => screen.id === openScreenId) || null : null
    },
    async selectProjectData(projectId) {
      const res = await fetchDesignWorkspace(projectId)
      this.workspace = res.data || emptyWorkspace()
    },
    async refreshProjectList() {
      const res = await fetchDesignProjects()
      this.projects = Array.isArray(res.data) ? res.data : []
    },
    formatFileSize(size) {
      if (!size) return "0 KB"
      return size < 1024 * 1024 ? `${(size / 1024).toFixed(1)} KB` : `${(size / 1024 / 1024).toFixed(1)} MB`
    },
    formatTime(value) {
      if (!value) return ""
      return String(value).replace("T", " ").slice(0, 16)
    },
    showToast(message, error = false) {
      clearTimeout(this.toastTimer)
      this.toast = message
      this.toastError = error
      this.toastTimer = setTimeout(() => { this.toast = "" }, 3000)
    }
  }
}
</script>

<style scoped>
.delivery-page { min-width: 0; color: #26323e; }
button, input, select { font: inherit; }
.page-head, .collection-head { min-height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 20px; }
.page-head h2, .collection-head h2 { margin: 0; font-size: 20px; letter-spacing: 0; }
.page-head p, .collection-head p { margin: 5px 0 0; color: #788490; font-size: 11px; }
.primary-button, .upload-button { min-height: 36px; display: inline-flex; align-items: center; justify-content: center; gap: 5px; padding: 0 13px; border: 0; border-radius: 6px; background: #1f6b4f; color: #fff; font-size: 12px; font-weight: 700; cursor: pointer; }
.collection-actions { display: flex; align-items: center; gap: 8px; }
.ai-image-button { min-height: 36px; padding: 0 13px; border: 1px solid #9dbdaf; border-radius: 6px; background: #f3f8f6; color: #245f49; font-size: 12px; font-weight: 700; cursor: pointer; }
.ai-image-button:hover { background: #e7f1ec; }
.upload-button.disabled { opacity: .65; cursor: wait; }
.folder-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(245px, 1fr)); gap: 12px; padding: 10px 0 28px; }
.folder-item { position: relative; min-width: 0; border: 1px solid #dfe4e8; border-radius: 7px; background: #fff; transition: border-color .16s, box-shadow .16s; }
.folder-item:hover { border-color: #abc4b9; box-shadow: 0 5px 16px rgba(34, 64, 52, .08); }
.folder-open { width: 100%; min-width: 0; display: grid; grid-template-columns: 62px minmax(0, 1fr); align-items: center; gap: 13px; padding: 16px 42px 16px 15px; border: 0; background: transparent; color: inherit; text-align: left; cursor: pointer; }
.folder-shape { position: relative; width: 58px; height: 43px; display: block; margin-top: 6px; border-radius: 5px; background: #d9a839; box-shadow: inset 0 -4px 0 rgba(107, 75, 11, .08); }
.folder-shape::before { content: ""; position: absolute; left: 0; top: -8px; width: 28px; height: 12px; border-radius: 5px 5px 0 0; background: #d9a839; }
.folder-shape span { position: absolute; inset: 4px 5px 5px; border-radius: 3px; background: #edc45b; }
.folder-copy { min-width: 0; display: block; }
.folder-copy strong, .folder-copy small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.folder-copy strong { margin-bottom: 8px; color: #25313c; font-size: 14px; }
.folder-theme { max-width: 100%; display: inline-block; overflow: hidden; margin-bottom: 2px; padding: 3px 6px; border-radius: 4px; background: #edf4f1; color: #28664f; text-overflow: ellipsis; white-space: nowrap; font-size: 9px; font-weight: 700; }
.folder-copy small { margin-top: 4px; color: #7c8791; font-size: 10px; }
.folder-edit, .bare-icon { border: 0; background: transparent; color: #6a7680; cursor: pointer; }
.folder-edit { position: absolute; top: 8px; right: 8px; width: 30px; height: 30px; border-radius: 5px; font-size: 15px; }
.folder-edit:hover, .bare-icon:hover { background: #edf2ef; color: #225f48; }
.breadcrumb { min-width: 0; display: flex; align-items: center; gap: 7px; min-height: 38px; border-bottom: 1px solid #e4e8ec; color: #8a949d; font-size: 11px; }
.breadcrumb button { padding: 5px 0; border: 0; background: transparent; color: #3a765e; cursor: pointer; }
.breadcrumb strong { min-width: 0; overflow: hidden; color: #596570; text-overflow: ellipsis; white-space: nowrap; }
.collection-title { min-width: 0; display: flex; align-items: center; gap: 9px; }
.collection-title h2 { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.small-folder, .empty-folder { position: relative; display: inline-block; flex: 0 0 auto; border-radius: 3px; background: #e2b344; }
.small-folder { width: 27px; height: 20px; }
.small-folder::before, .empty-folder::before { content: ""; position: absolute; left: 0; top: -4px; width: 13px; height: 6px; border-radius: 3px 3px 0 0; background: inherit; }
.bare-icon { width: 30px; height: 30px; border-radius: 5px; font-size: 15px; }
.control-bar { min-height: 54px; display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 8px 0; border-top: 1px solid #edf0f2; }
.search-box { width: min(300px, 40vw); height: 34px; display: flex; align-items: center; gap: 7px; padding: 0 10px; border: 1px solid #d5dce1; border-radius: 6px; background: #fff; color: #81909b; }
.search-box:focus-within { border-color: #7da995; box-shadow: 0 0 0 2px rgba(31, 107, 79, .08); }
.search-box input { min-width: 0; flex: 1; border: 0; outline: 0; color: #2a3741; font-size: 11px; }
.status-tabs { display: inline-flex; padding: 3px; border-radius: 6px; background: #e9edef; }
.status-tabs button { height: 28px; padding: 0 11px; border: 0; border-radius: 4px; background: transparent; color: #65717d; font-size: 10px; cursor: pointer; }
.status-tabs button.active { background: #fff; color: #245f49; font-weight: 700; box-shadow: 0 1px 3px rgba(24, 35, 30, .1); }
.screen-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(165px, 1fr)); gap: 14px; padding: 6px 0 28px; }
.screen-file { min-width: 0; overflow: hidden; padding: 0; border: 1px solid #dfe4e8; border-radius: 7px; background: #fff; color: inherit; text-align: left; cursor: pointer; transition: transform .16s, border-color .16s, box-shadow .16s; }
.screen-file:hover { transform: translateY(-2px); border-color: #abc4b9; box-shadow: 0 7px 18px rgba(34, 64, 52, .09); }
.screen-thumb { position: relative; width: 100%; aspect-ratio: 4 / 3; display: grid; place-items: center; overflow: hidden; background: #eef1f3; }
.screen-thumb img { width: 100%; height: 100%; object-fit: cover; object-position: top; }
.screen-thumb img.is-wide-artwork { object-fit: contain; object-position: center; }
.status-mark { display: inline-flex; align-items: center; justify-content: center; padding: 4px 7px; border-radius: 4px; background: #fff2dc; color: #986019; font-size: 9px; font-weight: 700; }
.screen-thumb .status-mark { position: absolute; top: 7px; right: 7px; box-shadow: 0 1px 5px rgba(30, 37, 42, .12); }
.status-mark.done { background: #e6f3ed; color: #236d52; }
.screen-file__copy { min-width: 0; display: block; padding: 10px 11px 11px; }
.screen-file__copy strong, .screen-file__copy small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.screen-file__copy strong { color: #25313c; font-size: 12px; }
.screen-file__copy small { margin-top: 5px; color: #89939c; font-size: 9px; }
.page-state { min-height: 330px; display: grid; place-items: center; color: #7f8a95; font-size: 12px; }
.page-state--empty { align-content: center; gap: 14px; border: 1px dashed #ccd4da; border-radius: 7px; background: #fafbfb; }
.page-state--empty strong { color: #53606c; font-size: 14px; }
.empty-folder { width: 64px; height: 45px; margin-top: 8px; opacity: .72; }
.collection-empty { min-height: 280px; }
.detail-backdrop { position: fixed; inset: 0; z-index: 85; display: grid; place-items: center; padding: 24px; background: rgba(22, 29, 36, .5); }
.detail-dialog { width: min(1180px, calc(100vw - 48px)); max-height: calc(100vh - 48px); display: flex; flex-direction: column; overflow: hidden; border-radius: 8px; background: #fff; box-shadow: 0 24px 70px rgba(17, 25, 31, .24); }
.detail-head { flex: 0 0 auto; min-height: 66px; display: grid; grid-template-columns: minmax(0, 1fr) auto 36px; align-items: center; gap: 14px; padding: 12px 18px; border-bottom: 1px solid #e5e9ec; }
.detail-head > div:first-child { min-width: 0; display: flex; align-items: center; gap: 9px; }
.detail-head h3 { min-width: 0; margin: 0; overflow: hidden; color: #26323d; text-overflow: ellipsis; white-space: nowrap; font-size: 16px; letter-spacing: 0; }
.detail-head > button { width: 34px; height: 34px; border: 0; border-radius: 5px; background: transparent; color: #68747f; font-size: 23px; cursor: pointer; }
.detail-head > button:hover { background: #f0f2f3; }
.detail-body { min-height: 0; height: min(74vh, 760px); display: grid; grid-template-columns: minmax(0, 1.65fr) minmax(330px, .75fr); }
.detail-preview-pane { min-width: 0; min-height: 0; display: flex; align-items: center; justify-content: center; overflow: hidden; padding: 18px; background: #eef1f3; }
.detail-preview { position: relative; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; overflow: hidden; padding: 0; border: 0; border-radius: 6px; background: #e6eaec; cursor: zoom-in; }
.detail-preview img { display: block; width: auto; height: auto; max-width: 100%; max-height: 100%; object-fit: contain; }
.detail-preview span { position: absolute; right: 9px; bottom: 9px; padding: 5px 7px; border-radius: 4px; background: rgba(28, 36, 42, .78); color: #fff; font-size: 9px; }
.detail-actions { display: flex; align-items: center; justify-content: flex-end; gap: 8px; }
.detail-actions button { min-height: 34px; border-radius: 5px; font-size: 10px; white-space: nowrap; cursor: pointer; }
.prompt-copy-button { padding: 0 12px; border: 1px solid #768895; background: #344955; color: #fff; font-weight: 700; }
.prompt-copy-button:hover { background: #263b46; }
.prompt-copy-button.copied { border-color: #9fc5b4; background: #e8f4ee; color: #236b50; }
.completion-button { padding: 0 12px; border: 0; background: #1f6b4f; color: #fff; font-weight: 700; }
.completion-button--undo { border: 1px solid #bed4ca; background: #edf6f2; color: #2b6c54; }
.plain-button { height: 36px; padding: 0 13px; border: 1px solid #d3dae0; border-radius: 6px; background: #fff; color: #53606c; cursor: pointer; }
.delete-button { padding: 0 9px; border: 0; background: transparent; color: #ae4c40; }
.detail-dialog .icon-section { min-width: 0; min-height: 0; overflow-y: auto; padding: 18px; border-left: 1px solid #e5e9ec; }
.icon-section > header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.icon-section > header > div { display: flex; align-items: baseline; gap: 7px; }
.icon-section h4 { margin: 0; font-size: 13px; letter-spacing: 0; }
.icon-section header span { color: #8a949e; font-size: 9px; }
.icon-section header button { height: 30px; padding: 0 9px; border: 1px solid #b7cec4; border-radius: 5px; background: #fff; color: #24664d; font-size: 10px; cursor: pointer; }
.icon-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 5px 14px; margin-top: 10px; }
.icon-item { min-width: 0; display: grid; grid-template-columns: 42px minmax(0, 1fr) 24px 22px; align-items: center; gap: 6px; padding: 7px 0; border-bottom: 1px solid #eef1f3; }
.icon-preview { width: 42px; height: 42px; display: grid; place-items: center; overflow: hidden; padding: 0; border: 0; border-radius: 5px; background: #eef1f3; cursor: pointer; }
.icon-preview img { max-width: 100%; max-height: 100%; object-fit: contain; }
.icon-copy { min-width: 0; }
.icon-copy strong, .icon-copy span { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.icon-copy strong { font-size: 10px; }
.icon-copy span { margin-top: 3px; color: #8a949e; font-size: 8px; }
.icon-item a, .icon-delete { width: 24px; height: 24px; display: grid; place-items: center; padding: 0; border: 0; border-radius: 4px; background: transparent; color: #50606c; text-decoration: none; cursor: pointer; }
.icon-item a:hover { background: #edf4f1; color: #22634b; }
.icon-delete { color: #b15145; font-size: 17px; }
.empty-icons { width: 100%; min-height: 64px; margin-top: 10px; border: 1px dashed #c8d2d8; border-radius: 5px; background: #f8faf9; color: #537164; font-size: 10px; cursor: pointer; }
.empty-icons--readonly { display: grid; place-items: center; color: #8a949d; cursor: default; }
.modal-backdrop { position: fixed; inset: 0; z-index: 90; display: grid; place-items: center; padding: 18px; background: rgba(22, 29, 36, .42); }
.modal-panel { width: min(440px, 100%); padding: 20px; border-radius: 8px; background: #fff; box-shadow: 0 22px 60px rgba(12, 18, 24, .2); }
.modal-panel--small { width: min(390px, 100%); }
.modal-panel header { display: flex; align-items: center; justify-content: space-between; }
.modal-panel h3 { margin: 0; font-size: 17px; letter-spacing: 0; }
.modal-panel header button { border: 0; background: transparent; color: #68747f; font-size: 22px; cursor: pointer; }
.modal-panel label { display: block; margin-top: 16px; }
.modal-panel label span { display: block; margin-bottom: 6px; color: #64717d; font-size: 10px; font-weight: 700; }
.modal-panel input, .modal-panel select { width: 100%; height: 38px; padding: 0 10px; border: 1px solid #d3dae0; border-radius: 5px; color: #27333e; }
.modal-panel footer { display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; }
.lightbox { position: fixed; inset: 0; z-index: 100; overflow: auto; padding: 18px; background: rgba(14, 19, 24, .9); text-align: center; }
.lightbox header { position: sticky; top: 0; z-index: 1; width: min(1100px, 96vw); display: flex; align-items: center; justify-content: space-between; margin: 0 auto 10px; color: #fff; }
.lightbox header button { border: 0; background: transparent; color: #fff; font-size: 26px; cursor: pointer; }
.lightbox img { max-width: min(1100px, 96vw); height: auto; background: #fff; box-shadow: 0 16px 50px rgba(0, 0, 0, .35); }
.upload-toast { position: fixed; right: 24px; bottom: 24px; z-index: 120; width: 220px; overflow: hidden; padding: 13px; border-radius: 6px; background: #263d34; color: #fff; font-size: 10px; }
.upload-toast span { position: absolute; left: 0; bottom: 0; height: 3px; background: #67bc95; }
.upload-toast strong { position: relative; }
.toast { position: fixed; right: 24px; bottom: 24px; z-index: 121; max-width: 340px; padding: 11px 14px; border-radius: 6px; background: #274b3f; color: #fff; font-size: 11px; box-shadow: 0 10px 26px rgba(20, 37, 31, .2); }
.toast--error { background: #a64438; }
@media (max-width: 700px) {
  .page-head, .collection-head { align-items: flex-start; flex-direction: column; padding: 9px 0 12px; }
  .collection-head .upload-button { align-self: flex-end; }
  .folder-grid { grid-template-columns: 1fr; gap: 9px; }
  .folder-open { min-height: 88px; }
  .control-bar { align-items: stretch; flex-direction: column; }
  .search-box { width: 100%; }
  .status-tabs { width: 100%; }
  .status-tabs button { flex: 1; padding: 0 5px; }
  .screen-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 9px; }
  .screen-file__copy { padding: 9px; }
  .screen-file__copy small { font-size: 8px; }
  .detail-backdrop { padding: 0; }
  .detail-dialog { width: 100vw; height: 100vh; max-height: none; border-radius: 0; }
  .detail-head { grid-template-columns: minmax(0, 1fr) 34px; padding: 10px 12px; }
  .detail-head .detail-actions { grid-column: 1 / -1; grid-row: 2; justify-content: flex-start; }
  .detail-head > button { grid-column: 2; grid-row: 1; }
  .detail-body { height: auto; display: block; overflow-y: auto; }
  .detail-preview-pane { height: 54vh; padding: 12px; }
  .detail-dialog .icon-section { overflow: visible; padding: 15px 13px 24px; border-top: 1px solid #e5e9ec; border-left: 0; }
  .icon-grid { grid-template-columns: 1fr; }
  .detail-actions { flex-wrap: wrap; }
  .completion-button { flex: 1; }
}
</style>
